import { inject, Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, fromEvent, Observable, Subscription } from 'rxjs';
import { SiaWindowSize } from '../interfaces/sia-window-size';

@Injectable({
  providedIn: 'root',
})
export class SiaSizeDetectorService {
  private zone = inject(NgZone);

  private _previousSizeSnapshot?: SiaWindowSize;

  private _contentBody: HTMLElement | null = null;
  private _topbarContainer: HTMLElement | null = null;
  private _headerBarTitle: HTMLElement | null = null;
  private _departmentHeader: HTMLElement | null = null;
  private _actionContainerMobile: HTMLElement | null = null;

  private _resizeSubject: BehaviorSubject<SiaWindowSize> =
    new BehaviorSubject<SiaWindowSize>({});

  private _renderPopupSubject: BehaviorSubject<{
    guid?: string;
    open?: boolean;
  }> = new BehaviorSubject({});

  isHoverable: boolean = true;
  isPortrait: boolean = false;
  currentSize: SiaWindowSize = {
    width: document.documentElement.clientWidth,
    height: document.documentElement.clientHeight,
    contentWidth: this._contentBody?.offsetWidth,
    contentHeight: this._contentBody?.offsetHeight,
    isMobile:
      document.documentElement.clientWidth < 768 ||
      (document.documentElement.clientWidth < 1024 &&
        window.matchMedia('(orientation:portrait)').matches),
    deviceType:
      document.documentElement.clientWidth < 768
        ? 'MOBILE'
        : document.documentElement.clientWidth < 1024
          ? 'TABLET'
          : 'DESKTOP',
  };
  resizeSubscription!: Subscription;
  private _contentCheckTimer: any;
  observer?: ResizeObserver;
  private _suppressedUntil = 0;
  private _suppressionTimer?: ReturnType<typeof setTimeout>;
  private _rafId?: number;

  /** Inserted by Angular inject() migration for backwards compatibility */

  constructor() {
    this.isHoverable = window.matchMedia('not all and (hover: none)').matches;
    this.detectScreenSize = this.detectScreenSize.bind(this);
    this.zone.runOutsideAngular(() => {
      this.resizeSubscription = fromEvent(window, 'resize').subscribe(
        (event) => {
          this.detectScreenSize(event);
        },
      );
    });
    this.detectScreenSize(null, true);
  }

  observerContent() {
    if (this._contentBody) {
      this.observer?.unobserve(this._contentBody);
    }

    this.observer = new ResizeObserver((entries) => {
      if (entries.length > 0) {
        this.detectScreenSize();
      }
    });
    if (this._contentBody) {
      this.observer.observe(this._contentBody);
    }
  }

  suppressResizeNotifications(duration = 350) {
    this._suppressedUntil = performance.now() + duration;
    if (this._suppressionTimer) {
      clearTimeout(this._suppressionTimer);
    }
    this._suppressionTimer = setTimeout(() => {
      this._suppressedUntil = 0;
      this.detectScreenSize(null, true);
    }, duration);
  }

  detectScreenSize($event: any = null, force: boolean = false) {
    if (
      !force &&
      this._suppressedUntil &&
      performance.now() < this._suppressedUntil
    ) {
      return;
    }
    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
    }
    this._rafId = requestAnimationFrame(() => {
      this._rafId = undefined;
      if (
        !force &&
        this._suppressedUntil &&
        performance.now() < this._suppressedUntil
      ) {
        return;
      }
      const root = document.documentElement;
      if (!this._contentBody || this._contentBody.offsetHeight === 0) {
        this._contentBody = document.querySelector('#contentBody');
        // Get other elements for top/bottom area height calculations
        this._topbarContainer = document.querySelector('#topbarContainer');
        this._headerBarTitle = document.querySelector('#headerBarTitle');
        this._departmentHeader = document.querySelector('#departmentHeader');
        this._actionContainerMobile = document.querySelector(
          '#actionContainerMobile',
        );

        this.observerContent();
        clearTimeout(this._contentCheckTimer);
        this._contentCheckTimer = setTimeout(() => {
          this.detectScreenSize();
        }, 500);
        return;
      }
      const width = root.clientWidth;
      const height = root.clientHeight;
      this.isPortrait = window.matchMedia('(orientation:portrait)').matches;
      const isMobile = width < 768 || (width < 1024 && this.isPortrait);

      const res: SiaWindowSize = {
        width,
        height,
        contentWidth: this._contentBody?.offsetWidth,
        contentHeight: this._contentBody?.offsetHeight,
        isMobile: isMobile,
        isHoverable: this.isHoverable,
        isPortrait: this.isPortrait,
        deviceType:
          width < 768 ? 'MOBILE' : width < 1024 ? 'TABLET' : 'DESKTOP',
        topAreaHeight: isMobile
          ? (this._headerBarTitle?.offsetHeight || 0) +
            (this._topbarContainer?.offsetHeight || 0) +
            (this._departmentHeader?.offsetHeight || 0)
          : 0,
        bottomAreaHeight: this._actionContainerMobile?.offsetHeight || 0,
      };
      if (
        !force &&
        this._previousSizeSnapshot &&
        this._sizesAreEqual(this._previousSizeSnapshot, res)
      ) {
        return;
      }
      this._previousSizeSnapshot = { ...res };
      this.zone.run(() => {
        this.currentSize = res;
        this._resizeSubject.next(res);
      });
    });
  }

  get onResize$(): Observable<SiaWindowSize | null> {
    return this._resizeSubject.asObservable();
  }

  get onRenderPopup$(): Observable<{ guid?: string; open?: boolean }> {
    return this._renderPopupSubject.asObservable();
  }

  renderPopup(menuId: string, open: boolean) {
    this._renderPopupSubject.next({ guid: menuId, open: open });
  }
  ngOnDestroy() {
    this.resizeSubscription.unsubscribe();
    this.observer?.disconnect();
    if (this._suppressionTimer) {
      clearTimeout(this._suppressionTimer);
    }
    if (this._contentCheckTimer) {
      clearTimeout(this._contentCheckTimer);
    }
    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
    }
  }

  private _sizesAreEqual(prev: SiaWindowSize, next: SiaWindowSize): boolean {
    return (
      prev.width === next.width &&
      prev.height === next.height &&
      prev.contentWidth === next.contentWidth &&
      prev.contentHeight === next.contentHeight &&
      prev.isMobile === next.isMobile &&
      prev.isHoverable === next.isHoverable &&
      prev.isPortrait === next.isPortrait &&
      prev.deviceType === next.deviceType
    );
  }
}
